import type { Voice } from 'ml-text-service-client';
import { useMemo } from 'react';

export const LANGUAGE_CODE_TO_NAME: Record<string, string> = {
    ar: 'Arabic',
    bg: 'Bulgarian',
    zh: 'Chinese',
    hr: 'Croatian',
    cs: 'Czech',
    da: 'Danish',
    nl: 'Dutch',
    en: 'English',
    fil: 'Filipino',
    fi: 'Finnish',
    fr: 'French',
    de: 'German',
    el: 'Greek',
    hi: 'Hindi',
    id: 'Indonesian',
    it: 'Italian',
    ja: 'Japanese',
    ko: 'Korean',
    ms: 'Malay',
    pl: 'Polish',
    pt: 'Portuguese',
    ro: 'Romanian',
    ru: 'Russian',
    sk: 'Slovak',
    es: 'Spanish',
    sv: 'Swedish',
    ta: 'Tamil',
    tr: 'Turkish',
    uk: 'Ukrainian',
};

export function getVoiceLanguageCodes(voice: Voice): string[] {
    const codes = new Set((voice.verified_languages ?? []).map((vl) => vl.language));
    codes.add('en');
    return Array.from(codes);
}

export function useVoiceoverFilters(results: Voice[]): {
    gender: string[];
    tone: string[];
    useCase: string[];
    accent: string[];
    language: string[];
} {
    const filters = useMemo(() => {
        const filterOptions = {
            gender: new Set<string>(),
            tone: new Set<string>(),
            useCase: new Set<string>(),
            accent: new Set<string>(),
            language: new Set<string>(),
        };

        results.forEach((item) => {
            if (item.gender) {
                filterOptions.gender.add(item.gender);
            }
            if (item.descriptive) {
                filterOptions.tone.add(item.descriptive);
            }
            if (item.use_case) {
                filterOptions.useCase.add(item.use_case);
            }
            if (item.accent) {
                filterOptions.accent.add(item.accent);
            }
            getVoiceLanguageCodes(item).forEach((code) => {
                const name = LANGUAGE_CODE_TO_NAME[code];
                if (name) {
                    filterOptions.language.add(name);
                }
            });
        });

        return {
            gender: Array.from(filterOptions.gender),
            tone: Array.from(filterOptions.tone),
            useCase: Array.from(filterOptions.useCase),
            accent: Array.from(filterOptions.accent),
            language: Array.from(filterOptions.language).sort(),
        };
    }, [results]);

    return filters;
}

export function validateFileSize(file: File, setError: (err: string) => void): boolean {
    if (file.size > 10 * 1024 * 1024) {
        setError('File size must be less than 10MB');
        return false;
    }
    return true;
}

export function formatDisplayText(text: string | null | undefined): string {
    if (!text) return '';
    const result = text.replace(/_/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase());

    return result.replace(/\bTv\b/g, 'TV');
}
